import { FC, Fragment, ReactNode } from 'react'
import { CategoryState, ConfirmationState, SideoverState, TagState, UserState } from '@/states'
import { useNotification } from '@/hooks'
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/16/solid'
import { Delete as DeleteTag } from '@/controllers/tag'
import { Delete as DeleteCategory } from '@/controllers/category'

import {
    BaseTimeFormat,
    CATEGORY_DEFAULT_ERRORS,
    COMPONENT_DEFAULT_ERRORS,
    TAG_DEFAULT_ERRORS
} from '@/data'

const TableItem: FC<TableComponentProps> = (props): ReactNode => {
    const {
        list,
        target,
        deleting,
        setDeleting
    } = props

    const { User } = UserState()
    const { SetState, Target } = SideoverState()
    const { InitNotification } = useNotification()
    const { SetState: ConfirmationSetState } = ConfirmationState()
    const { Categories, SetState: SetCategories } = CategoryState()
    const { Tags, SetState: SetTags } = TagState()

    const HandleEdit = (id: string) => {
        if (User && deleting === false) {
            const item = list.find((item: CategoryInterface | TagInterface) => item._id === id)
            let errors: {[key: string]: boolean} = COMPONENT_DEFAULT_ERRORS

            if (item?._id) {
                if (Target === 'component') errors = COMPONENT_DEFAULT_ERRORS
                if (Target === 'tag') errors = TAG_DEFAULT_ERRORS
                if (Target === 'category') errors = CATEGORY_DEFAULT_ERRORS

                SetState({
                    Open: true,
                    Target: target,
                    Mode: 'edit',
                    Form: item,
                    Errors: errors
                })
            }

            else InitNotification('Something went wrong opening the selected item!')
        }
    }

    const HandleDelete = (name: string, id: string) => {
        if (User && deleting === false) {
            ConfirmationSetState({
                Title: `Are you sure you want to delete '${name}' ${target === 'tag' ? 'tag' : target === 'category' ? 'category' : ''}? This action is irreversible!`,
                Open: true,
                onConfirm: () => {
                    if (target === 'tag') DeleteTag(
                        id,
                        Tags,
                        SetTags,
                        setDeleting,
                        InitNotification
                    )

                    if (target === 'category') DeleteCategory(
                        id,
                        Categories,
                        SetCategories,
                        setDeleting,
                        InitNotification
                    )

                    ConfirmationSetState({
                        Open: false,
                        Title: '',
                        onClick: () => {}
                    })
                }
            })
        }
    }

    return list?.map((item: CategoryInterface | TagInterface, index: number) => {
        const isLast = index === list?.length - 1

        return (
            <tr key={item?._id} className={`bg-[#42424225] w-full ${!isLast ? 'border-b border-[#42424275]' : 'rounded-b-md'}`}>
                <td className='w-[71%] py-4 px-6 text-[#EBEBEB] font-medium'>
                    {item?.Name}

                    <span className='text-xs text-[#cfcece] ml-2'>
                        {/* @ts-ignore */}
                        {item?.hasOwnProperty('Components') ? `(${item?.Components} componenets)` : ''}
                    </span>
                </td>

                <td className='w-[15%] py-4 px-6 text-[#EBEBEB] font-medium text-end hidden lg:table-cell'>
                    {BaseTimeFormat(item?.Updated_At)}
                </td>

                <td className='relative w-auto py-4 px-6 flex justify-end items-center gap-2 text-end'>
                    {
                        deleting === item._id &&
                        <svg className='w-5 h-5 text-[#EBEBEB] animate-spin fill-[#40404090] ignore-blur' viewBox='0 0 100 101' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path className='ignore-blur' d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z' fill='currentColor'/>
                            <path className='ignore-blur' d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z' fill='currentFill'/>
                        </svg>
                    }

                    {
                        deleting !== item._id &&
                        <Fragment>
                            <div onClick={() => HandleDelete(item?.Name, item._id)} className='min-w-[28px] min-h-[28px] max-w-[28px] max-h-[28px] bg-[#404040] hover:bg-[#5a5a5a] text-[#EBEBEB] hover:text-[#EBEBEB] rounded-lg flex items-center justify-center cursor-pointer transition-all duration-500 ease-in-out z-[11]'>
                                <TrashIcon className='w-4 h-4' />
                            </div>

                            <div onClick={() => HandleEdit(item._id)} className='min-w-[28px] min-h-[28px] max-w-[28px] max-h-[28px] bg-[#404040] hover:bg-[#5a5a5a] text-[#EBEBEB] hover:text-[#EBEBEB] rounded-lg flex items-center justify-center cursor-pointer transition-all duration-500 ease-in-out z-[11]'>
                                <PencilSquareIcon className='w-4 h-4' />
                            </div>
                        </Fragment>
                    }
                </td>
            </tr>
        )
    })
}

export default TableItem
